import {Box} from "@mui/material";
import {SupervisorAccountOutlined as GroupAdminIcon} from "@mui/icons-material";
import {useCallback, useEffect, useState} from "react";
import AddOwnerDialog, {AvailabilityCheck, OwnerSubmitPayload} from "../company/AddOwnerDialog";
import ImpersonationCompaniesPicker from "./ImpersonationCompaniesPicker";
import {
    checkCompanyGroupAdminAvailability,
    createCompanyGroupAdmin,
} from "../../services/system/CompanyGroupAdminService";
import {useMessageContext} from "../../contexts/MessageContext";
import {colors, borders} from "../ui/colors";

export type AddCompanyGroupAdminDialogProps = {
    open: boolean;
    close: () => void;
    setReload?: (updater: (prev: boolean) => boolean) => void;
};

// Add-a-Company-Group-Administrator drawer. Reuses the company Add-Owner flow (full name, email,
// phone, password + confirm) via <AddOwnerDialog> and injects the shared "companies authorized for
// impersonation" picker as extra content. A group admin is NOT tied to any company, and for now only
// brand-new users can be created (the "existing user" path is disabled).
//
// The `checkAvailability` probe wires the form's on-blur validation to the platform-level
// /system/companyGroupAdmin/check-availability endpoint, so a duplicate email/phone is reported as the
// user leaves the field instead of as a generic red toast after submit.
const AddCompanyGroupAdminDialog = ({open, close, setReload}: AddCompanyGroupAdminDialogProps) => {
    const notify = useMessageContext()?.notificationHandler;

    const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);

    useEffect(() => {
        if (!open) return;
        setSelectedCompanyIds([]);
    }, [open]);

    const handleClose = () => {
        setSelectedCompanyIds([]);
        close();
    };

    // Stable identity so AddOwnerDialog's blur handler isn't re-created on every render. Forwards
    // `name` too so the dialog can flag a duplicate group-admin display name on blur, like email/phone.
    const checkAvailability = useCallback<AvailabilityCheck>(
        ({email, phoneNumber, name}) => checkCompanyGroupAdminAvailability({email, phoneNumber, name}),
        [],
    );

    // Owner payload arrives from AddOwnerDialog; merge the selected impersonation companies.
    const handleSubmit = (ownerPayload: OwnerSubmitPayload) => {
        createCompanyGroupAdmin({
            name: ownerPayload.name,
            email: ownerPayload.email,
            phoneNumber: ownerPayload.phoneNumber,
            password: ownerPayload.password ?? "",
            companyIds: selectedCompanyIds,
        })
            .then(() => notify?.success("מנהל קבוצת חברות נוצר בהצלחה"))
            .then(() => setReload?.((prev: boolean) => !prev))
            .then(handleClose)
            .catch(() => notify?.error("אירעה שגיאה ביצירת מנהל קבוצת חברות"));
    };

    const impersonationSection = (
        <Box sx={{marginTop: "20px", paddingTop: "20px", borderTop: borders.default}}>
            <ImpersonationCompaniesPicker
                active={open}
                value={selectedCompanyIds}
                onChange={setSelectedCompanyIds}
            />
        </Box>
    );

    return (
        <AddOwnerDialog
            open={open}
            close={handleClose}
            companyId={null}
            handleSubmit={handleSubmit}
            title="מנהל קבוצת חברות חדש"
            titleIcon={<GroupAdminIcon sx={{color: colors.textSecondary, fontSize: "20px"}}/>}
            allowExistingUser={false}
            checkAvailability={checkAvailability}
            validateNameUniqueness
        >
            {impersonationSection}
        </AddOwnerDialog>
    );
};

export default AddCompanyGroupAdminDialog;
